// Framework-free upload queue, SHARED by every ingest flow — photo ingest
// (#323) and the multi-video footage upload behind `multiVideoV1`. It knows
// nothing about what an item IS: it moves bytes, retries, cancels and reports.
// Both flows drive this one implementation rather than forking a second copy.
//
// Contract:
//   - concurrency: at most `concurrency` uploads in flight at once
//   - retry: up to `maxAttempts` per file with exponential backoff + jitter
//   - cancel: pending items dropped, in-flight items get a `cancel` signal
//   - events: emits 'progress' (per-file + aggregate), 'itemDone', 'itemFailed',
//     'done' (all items finished, one way or another). Events fire via a
//     simple subscribe(callback) API — no external EventEmitter dep so the
//     module stays framework-free and unit-testable with fake timers.
//
// The uploader function is injected: `uploadItem(item, { onProgress, signal })`
// returns a Promise. That keeps this class free of any Firebase types — the
// real caller wires up Firebase Storage; tests use a plain jest fn.

const DEFAULT_CONCURRENCY = 5;
const DEFAULT_MAX_ATTEMPTS = 3;
// A single upload that stops making progress for this long is treated as
// stalled — aborted and routed through the normal retry/fail path. Without
// this, one silently-hung upload (a half-open TCP socket, or an SDK task that
// never fires error OR complete) keeps its in-flight slot forever, so the
// queue never settles and the whole batch UI freezes mid-upload. 90s is far
// longer than any single photo needs while still bounding a true hang.
const DEFAULT_STALL_TIMEOUT_MS = 90 * 1000;

// Exponential backoff with full jitter. 500ms * 2^(attempt-1) capped at 8s.
function defaultBackoffMs(attempt) {
  const base = Math.min(8000, 500 * (2 ** (attempt - 1)));
  return Math.random() * base;
}

// Marker error so the retry path and tests can tell a stall from a real
// upload rejection.
export class UploadStallError extends Error {
  constructor(stallMs) {
    super(`upload stalled: no progress for ${stallMs}ms`);
    this.name = 'UploadStallError';
    this.stalled = true;
  }
}

const STATES = {
  IDLE: 'idle',
  RUNNING: 'running',
  CANCELLED: 'cancelled',
  DONE: 'done',
};

// EMA smoothing for the throughput reading. 0.3 = new samples get 30% weight.
const RATE_EMA_ALPHA = 0.3;

export default class UploadQueue {
  constructor({
    items,
    uploadItem,
    concurrency = DEFAULT_CONCURRENCY,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    backoffMs = defaultBackoffMs,
    stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
    // Wrap the globals so they keep their `window` receiver. Assigning the bare
    // `setTimeout`/`clearTimeout` and later calling them as `this._setTimeout(...)`
    // rebinds the receiver to the queue instance, which throws "Illegal
    // invocation" in browsers (Node is lenient). Tests inject their own fns.
    setTimeoutFn = (fn, ms) => setTimeout(fn, ms),
    clearTimeoutFn = (id) => clearTimeout(id),
    now = () => Date.now(),
  } = {}) {
    if (typeof uploadItem !== 'function') {
      throw new Error('UploadQueue: uploadItem must be a function');
    }
    this._uploadItem = uploadItem;
    this._concurrency = Math.max(1, concurrency);
    this._maxAttempts = Math.max(1, maxAttempts);
    this._backoffMs = backoffMs;
    // <= 0 disables the stall watchdog (tests that don't exercise it).
    this._stallTimeoutMs = stallTimeoutMs > 0 ? stallTimeoutMs : 0;
    this._setTimeout = setTimeoutFn;
    this._clearTimeout = clearTimeoutFn;
    this._now = now;

    this._items = (items || []).map((it, idx) => ({
      ...it,
      _index: idx,
      _bytesTransferred: 0,
      _bytesTotal: Number.isFinite(it.sizeBytes) ? it.sizeBytes : 0,
      _attempts: 0,
      _status: 'queued',
      _controller: null,
      _retryHandle: null,
      _stallHandle: null,
      _lastActivityAt: 0,
    }));

    this._state = STATES.IDLE;
    this._listeners = new Set();
    this._pendingIdx = 0;
    this._inFlight = 0;
    this._startedAt = null;
    this._doneCount = 0;
    this._failedCount = 0;
    this._skippedCount = 0;
    this._lastRateSampleAt = null;
    this._lastRateSampleBytes = 0;
    this._smoothedRateBps = null;
    this._donePromise = null;
    this._resolveDone = null;
    this._finished = false;
  }

  get state() { return this._state; }
  get totalBytes() { return this._items.reduce((a, it) => a + it._bytesTotal, 0); }
  get totalItems() { return this._items.length; }

  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  start() {
    if (this._state !== STATES.IDLE) return this._donePromise;
    this._state = STATES.RUNNING;
    this._startedAt = this._now();
    this._lastRateSampleAt = this._startedAt;
    this._donePromise = new Promise((resolve) => { this._resolveDone = resolve; });
    this._pump();
    return this._donePromise;
  }

  cancel() {
    if (this._state !== STATES.RUNNING) return;
    this._state = STATES.CANCELLED;
    // Signal in-flight uploads and cancel any pending retries.
    for (const item of this._items) {
      if (item._retryHandle) {
        this._clearTimeout(item._retryHandle);
        item._retryHandle = null;
      }
      this._clearStall(item);
      if (item._controller && typeof item._controller.abort === 'function') {
        try { item._controller.abort(); } catch (_err) { /* ignore */ }
      }
      if (item._status === 'queued' || item._status === 'retrying') {
        // A 'retrying' item still holds its in-flight slot (released only when
        // the retry timer fires). We just cleared that timer, so release the
        // slot here — otherwise _inFlight never reaches 0 and start() hangs.
        if (item._status === 'retrying') {
          this._inFlight -= 1;
        }
        item._status = 'cancelled';
      }
    }
    this._maybeFinish();
  }

  _pump() {
    // Only launch new work while RUNNING, but ALWAYS re-check completion:
    // in-flight uploads (and pending retry timers) settle after a cancel and
    // route back through here, and that last settle is what has to fire
    // 'done' / resolve the promise. Early-returning before _maybeFinish would
    // strand the queue in 'cancelled' with an unresolved start() promise.
    if (this._state === STATES.RUNNING) {
      while (this._inFlight < this._concurrency && this._pendingIdx < this._items.length) {
        const item = this._items[this._pendingIdx++];
        if (item._status !== 'queued') continue;
        this._inFlight += 1;
        this._runOne(item);
      }
    }
    this._maybeFinish();
  }

  _clearStall(item) {
    if (item._stallHandle != null) {
      this._clearTimeout(item._stallHandle);
      item._stallHandle = null;
    }
  }

  async _runOne(item) {
    item._attempts += 1;
    item._status = 'uploading';
    item._controller = typeof AbortController === 'function' ? new AbortController() : { abort: () => {} };
    const signal = item._controller.signal;
    item._lastActivityAt = this._now();

    const onProgress = ({ bytesTransferred }) => {
      if (this._state === STATES.CANCELLED) return;
      // Any byte movement counts as liveness — resets the stall clock.
      item._lastActivityAt = this._now();
      const clamped = Math.min(item._bytesTotal, Math.max(0, bytesTransferred || 0));
      item._bytesTransferred = clamped;
      this._sampleRate();
      this._emitProgress(item);
    };

    try {
      const uploadPromise = this._uploadItem(item, { onProgress, signal });
      // If a stall makes us abandon this attempt, the underlying upload may
      // still settle later — swallow it so it can't surface as an unhandled
      // rejection.
      if (uploadPromise && typeof uploadPromise.catch === 'function') {
        uploadPromise.catch(() => {});
      }
      const result = this._stallTimeoutMs > 0
        ? await Promise.race([uploadPromise, this._watchStall(item)])
        : await uploadPromise;
      this._clearStall(item);
      if (this._state === STATES.CANCELLED) {
        item._status = 'cancelled';
        this._inFlight -= 1;
        this._pump();
        return;
      }
      item._bytesTransferred = item._bytesTotal;
      item._status = 'done';
      item._result = result || null;
      this._doneCount += 1;
      this._inFlight -= 1;
      this._emit('itemDone', item);
      this._sampleRate();
      this._emitProgress(item);
      this._pump();
    } catch (err) {
      this._clearStall(item);
      if (this._state === STATES.CANCELLED) {
        item._status = 'cancelled';
        this._inFlight -= 1;
        this._pump();
        return;
      }
      item._lastError = err;
      if (item._attempts >= this._maxAttempts) {
        item._status = 'failed';
        this._failedCount += 1;
        this._inFlight -= 1;
        this._emit('itemFailed', item);
        this._pump();
        return;
      }
      // Retry with backoff. Keep the item's in-flight slot so we don't
      // over-launch: the slot is released only after the retry completes.
      item._status = 'retrying';
      const delay = this._backoffMs(item._attempts);
      item._retryHandle = this._setTimeout(() => {
        item._retryHandle = null;
        if (this._state !== STATES.RUNNING) {
          this._inFlight -= 1;
          this._pump();
          return;
        }
        this._runOne(item);
      }, delay);
    }
  }

  // A promise that rejects with UploadStallError when the in-flight upload
  // stops making progress for `stallTimeoutMs`. It never resolves — the winner
  // of the race is always the real upload unless it stalls. The timer re-arms
  // for the remaining window whenever progress has landed since it was set, so
  // a slow-but-moving upload is never killed. Aborting the controller stops the
  // underlying transfer; the reject drives the normal retry/fail path.
  _watchStall(item) {
    return new Promise((_resolve, reject) => {
      const tick = () => {
        item._stallHandle = null;
        if (item._status !== 'uploading' || this._state !== STATES.RUNNING) return;
        const idle = this._now() - item._lastActivityAt;
        if (idle >= this._stallTimeoutMs) {
          try { item._controller.abort(); } catch (_err) { /* ignore */ }
          reject(new UploadStallError(this._stallTimeoutMs));
          return;
        }
        item._stallHandle = this._setTimeout(tick, this._stallTimeoutMs - idle);
      };
      item._stallHandle = this._setTimeout(tick, this._stallTimeoutMs);
    });
  }

  _sampleRate() {
    const now = this._now();
    const dt = (now - (this._lastRateSampleAt || now)) / 1000;
    if (dt < 0.5) return; // Don't sample more than 2x per second.
    const currentBytes = this._items.reduce((a, it) => a + it._bytesTransferred, 0);
    const db = currentBytes - this._lastRateSampleBytes;
    this._lastRateSampleAt = now;
    this._lastRateSampleBytes = currentBytes;
    if (db <= 0 || dt <= 0) return;
    const instantaneous = db / dt;
    if (this._smoothedRateBps == null) {
      this._smoothedRateBps = instantaneous;
    } else {
      this._smoothedRateBps = RATE_EMA_ALPHA * instantaneous + (1 - RATE_EMA_ALPHA) * this._smoothedRateBps;
    }
  }

  _snapshot() {
    const bytesTransferred = this._items.reduce((a, it) => a + it._bytesTransferred, 0);
    const totalBytes = this.totalBytes;
    const doneCount = this._doneCount;
    const failedCount = this._failedCount;
    const totalItems = this.totalItems;
    const inFlight = this._items.filter((it) => it._status === 'uploading').length;
    const retrying = this._items.filter((it) => it._status === 'retrying').length;
    const bytesPerSecond = this._smoothedRateBps || 0;
    const remainingBytes = Math.max(0, totalBytes - bytesTransferred);
    const etaSeconds = bytesPerSecond > 0 ? remainingBytes / bytesPerSecond : null;
    return {
      state: this._state,
      totalItems,
      doneCount,
      failedCount,
      inFlight,
      retrying,
      bytesTransferred,
      totalBytes,
      bytesPerSecond,
      etaSeconds,
      progressPct: totalBytes > 0 ? Math.min(100, (bytesTransferred / totalBytes) * 100) : 0,
    };
  }

  _emitProgress(item) {
    this._emit('progress', { item, aggregate: this._snapshot() });
  }

  _emit(type, payload) {
    for (const fn of this._listeners) {
      try { fn({ type, ...payload }); } catch (err) { /* subscribers must not break the pump */ console.error(err); }
    }
  }

  _maybeFinish() {
    if (this._finished) return; // 'done' is terminal + fires exactly once
    const settled = this._doneCount + this._failedCount + this._items.filter((it) => it._status === 'cancelled').length;
    if (this._inFlight === 0 && settled >= this._items.length) {
      this._finished = true;
      if (this._state !== STATES.CANCELLED) this._state = STATES.DONE;
      this._emit('done', { aggregate: this._snapshot() });
      if (this._resolveDone) {
        this._resolveDone(this._snapshot());
        this._resolveDone = null;
      }
    }
  }

  // For tests + UI polling. Copies (does not expose) item state.
  //
  // `key` is the flow-neutral identity every caller should read: photos pass
  // `photoKey`, footage videos pass `videoKey`, and either is echoed back
  // verbatim as well so the existing photo call sites keep working unchanged.
  getItems() {
    return this._items.map((it) => ({
      key: it.key ?? it.photoKey ?? it.videoKey ?? null,
      photoKey: it.photoKey,
      videoKey: it.videoKey,
      status: it._status,
      attempts: it._attempts,
      bytesTransferred: it._bytesTransferred,
      bytesTotal: it._bytesTotal,
      dayKey: it.dayKey,
      fileName: it.fileName,
      result: it._result || null,
      lastError: it._lastError || null,
    }));
  }

  getAggregate() {
    return this._snapshot();
  }
}
